/**
 * https://github.com/rsms/js-lru/blob/master/lru.js
 *
 * A doubly linked list-based Least Recently Used (LRU) cache. Will keep most
 * recently used items while discarding least recently used items when its limit
 * is reached.
 *
 * Licensed under MIT. Copyright (c) 2010 Rasmus Andersson <http://hunch.se/>
 * See README.md for details.
 *
 * Illustration of the design:
 *
 *       entry             entry             entry             entry
 *       ______            ______            ______            ______
 *      | head |.newer -> |      |.newer -> |      |.newer -> | tail |
 *      |  A   |          |  B   |          |  C   |          |  D   |
 *      |______| <- older.|______| <- older.|______| <- older.|______|
 *
 *  removed  <--  <--  <--  <--  <--  <--  <--  <--  <--  <--  <--  added
 *
 * @flow
 */

type LRUKey = string;

type LRUEntry<T> = {
  key: LRUKey;
  newer: LRUEntry<T> | null;
  older: LRUEntry<T> | null;
  value: T;
};

export default class LRU<T> {
  private limit: number;
  private size = 0;
  private tail: LRUEntry<T> | null = null;
  private head: LRUEntry<T> | null = null;
  private keymap: {[key: string]: LRUEntry<T>} = {};

  public constructor(limit: number) {
    this.limit = Math.ceil(limit);
  }

  /**
   * Put <value> into the cache associated with <key>. Returns the entry which was
   * removed to make room for the new entry. Otherwise *null* is returned
   * (i.e. if there was enough room already).
   */
  public put(key: LRUKey, value: T): LRUEntry<T> | null {
    let entry = this.get(key);
    if (entry) {
      // entry is already cached so let's just overwrite its value
      entry.value = value;
      return null;
    }

    entry = {
      key,
      value,
      newer: null,
      older: null
    };
    this.keymap[key] = entry;

    if (this.tail) {
      // link previous tail to the new tail (entry)
      this.tail.newer = entry;
      entry.older = this.tail;
    } else {
      // we're first in -- yay
      this.head = entry;
    }

    // add new entry to the end of the linked list -- it's now the freshest entry
    this.tail = entry;
    this.size++;
    if (this.size > this.limit) {
      // we hit the limit -- remove the head
      return this.shift();
    }

    return null;
  }

  /**
   * Purge the least recently used (oldest) entry from the cache. Returns the
   * removed entry or *null* if the cache was empty.
   *
   * If you need to perform any form of finalization of purged items, this is a
   * good place to do it. Simply override/replace this function:
   *
   *   var c = new LRUCache(123);
   *   c.shift = function() {
   *     var entry = LRUCache.prototype.shift.call(this);
   *     doSomethingWith(entry);
   *     return entry;
   *   }
   */
  public shift(): LRUEntry<T> | null {
    // TODO: handle special case when limit == 1
    const entry = this.head;
    if (entry) {
      if (entry.newer) {
        this.head = entry.newer;
        this.head.older = null;
      } else {
        this.head = null;
        this.tail = null;
      }
      // Remove last strong reference to <entry> and remove links from the purged
      // entry being returned:
      entry.newer = null;
      entry.older = null;
      // delete is slow, but we need to do this to avoid uncontrollable growth:
      delete this.keymap[entry.key];
      this.size--;
    }
    return entry;
  }

  /**
   * Get and do not register recent use of <key>. Returns the value associated with <key>
   * or null if not in cache.
   */
  public find(key: LRUKey): LRUEntry<T> | null {
    return this.keymap[key] || null;
  }

  /**
   * Get and register recent use of <key>. Returns the value associated with <key>
   * or null if not in cache.
   */
  public get(key: LRUKey): LRUEntry<T> | null {
    // First, find our cache entry
    const entry: LRUEntry<T> | null = this.keymap[key];
    if (!entry) { // Not cached. Sorry.
      return null;
    }
    // As <key> was found in the cache, register it as being requested recently
    if (entry === this.tail) {
      // Already the most recenlty used entry, so no need to update the list
      return entry;
    }

    // HEAD--------------TAIL
    //   <.older   .newer>
    //  <--- add direction --
    //   A  B  C  <D>  E
    if (entry.newer) {
      if (entry === this.head) {
        this.head = entry.newer;
      }
      entry.newer.older = entry.older; // C <-- E.
    }
    if (entry.older) {
      entry.older.newer = entry.newer; // C. --> E
    }
    entry.newer = null; // D --x
    entry.older = this.tail; // D. --> E
    if (this.tail) {
      this.tail.newer = entry; // E. <-- D
    }
    this.tail = entry;

    return entry;
  }

  /**
   * Returns values of LRU entries as a list ordered by recent usage -
   * from tail to head (from the newest to the oldest).
   */
  public toArray(): T[] {
    const arr = [];
    let entry = this.tail;
    while (entry) {
      arr.push(entry.value);
      entry = entry.older;
    }
    return arr;
  }

  /**
   * Returns LRU data in dictionary form.
   */
  public toMap(): {[key: string]: T} {
    const map: {[key: string]: T} = {};
    let entry = this.tail;
    while (entry) {
      map[entry.key] = entry.value;
      entry = entry.older;
    }
    return map;
  }
}
